if (!customElements.get('cf-product-recommendations')) {
  customElements.define(
    'cf-product-recommendations',
    class CfProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.abortController = new AbortController();
        this.grid = this.querySelector('[data-cf-grid]');
        this.dots = this.querySelector('[data-cf-dots]');
        this.arrows = this.querySelector('[data-cf-arrows]');
        this.previousButton = this.querySelector('[data-cf-prev]');
        this.nextButton = this.querySelector('[data-cf-next]');
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.isRtl = getComputedStyle(this).direction === 'rtl';

        if (!this.grid) return;

        const signal = this.abortController.signal;
        this.previousButton?.addEventListener('click', () => this.goToAdjacentPage(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.goToAdjacentPage(1), { signal });
        this.grid.addEventListener('scroll', () => this.queueControlUpdate(), { passive: true, signal });
        if (this.dataset.layout === 'carousel') {
          this.grid.addEventListener(
            'keydown',
            (event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              event.preventDefault();
              const previousKey = this.isRtl ? 'ArrowRight' : 'ArrowLeft';
              this.goToAdjacentPage(event.key === previousKey ? -1 : 1);
            },
            { signal }
          );
        }
        window.addEventListener('resize', () => this.queueLayoutUpdate(), { passive: true, signal });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.queueLayoutUpdate());
          this.resizeObserver.observe(this.grid);
        }

        this.finalize();
        if (this.dataset.recommendationsUrl) this.loadRecommendations();
      }

      async loadRecommendations() {
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(this.dataset.recommendationsUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortController.signal
          });

          if (!response.ok) throw new Error('Recommendation request failed');

          const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incomingGrid = responseDocument.querySelector(
            `[data-section-id="${CSS.escape(this.dataset.sectionId)}"] [data-cf-grid]`
          );

          if (incomingGrid) this.grid.innerHTML = incomingGrid.innerHTML;
        } catch (error) {
          if (error.name === 'AbortError') return;
          // Keep any configured static fallback already rendered in the section.
        } finally {
          this.removeAttribute('aria-busy');
        }

        this.finalize();
      }

      finalize() {
        const currentProductId = this.dataset.currentProduct;
        const hideUnavailable = this.dataset.hideUnavailable === 'true';
        const maximum = Number(this.dataset.limit) || 10;

        this.grid.querySelectorAll('[data-cf-card]').forEach((card) => {
          if (
            card.dataset.productId === currentProductId ||
            (hideUnavailable && card.dataset.available !== 'true')
          ) {
            card.remove();
          }
        });

        this.grid.querySelectorAll('[data-cf-card]').forEach((card, index) => {
          if (index >= maximum) card.remove();
        });

        const cards = this.grid.querySelectorAll('[data-cf-card]');
        if (!cards.length) {
          if (this.dataset.designMode !== 'true') this.hidden = true;
          this.setControlsVisibility(false);
          return;
        }

        this.hidden = false;
        this.rebuildPagination();
        this.updateControls();
      }

      getPagePositions() {
        if (this.dataset.layout !== 'carousel') return [0];

        const maximum = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
        if (maximum <= 2) return [0];

        const count = Math.max(2, Math.ceil(this.grid.scrollWidth / this.grid.clientWidth));
        const positions = [];

        for (let index = 0; index < count; index += 1) {
          const position = Math.min(index * this.grid.clientWidth, maximum);
          if (positions[positions.length - 1] !== position) positions.push(position);
        }

        return positions;
      }

      rebuildPagination() {
        this.pagePositions = this.getPagePositions();
        const hasMultiplePages = this.pagePositions.length > 1;
        this.setControlsVisibility(hasMultiplePages);

        if (!this.dots) return;

        this.dots.replaceChildren();
        this.activePage = null;
        this.pagePositions.forEach((position, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Go to product page ${index + 1}`);
          dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          dot.addEventListener(
            'click',
            () => this.scrollToPosition(position),
            { signal: this.abortController.signal }
          );
          this.dots.append(dot);
        });
      }

      setControlsVisibility(visible) {
        if (this.arrows) this.arrows.hidden = !visible;
        if (this.dots) this.dots.hidden = !visible;
      }

      get scrollBehavior() {
        return this.prefersReducedMotion ? 'auto' : 'smooth';
      }

      getActivePage() {
        const current = this.getScrollPosition();
        let activePage = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        (this.pagePositions || [0]).forEach((position, index) => {
          const distance = Math.abs(current - position);
          if (distance < smallestDistance) {
            activePage = index;
            smallestDistance = distance;
          }
        });

        return activePage;
      }

      goToAdjacentPage(direction) {
        const positions = this.pagePositions || [0];
        const activePage = this.getActivePage();
        const targetPage = Math.max(0, Math.min(activePage + direction, positions.length - 1));
        this.scrollToPosition(positions[targetPage]);
      }

      getScrollPosition() {
        const position = this.grid?.scrollLeft || 0;
        return this.isRtl ? Math.abs(position) : position;
      }

      scrollToPosition(position) {
        this.grid.scrollTo({ left: this.isRtl ? -position : position, behavior: this.scrollBehavior });
      }

      updateControls() {
        const activePage = this.getActivePage();
        const lastPage = Math.max(0, (this.pagePositions || [0]).length - 1);

        if (this.previousButton) this.previousButton.disabled = activePage === 0;
        if (this.nextButton) this.nextButton.disabled = activePage === lastPage;

        let activeDot;
        this.dots?.querySelectorAll('button').forEach((dot, index) => {
          dot.setAttribute('aria-current', index === activePage ? 'true' : 'false');
          if (index === activePage) activeDot = dot;
        });

        if (this.activePage !== activePage) {
          activeDot?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
          this.activePage = activePage;
        }
      }

      queueControlUpdate() {
        if (this.controlFrame) return;
        this.controlFrame = requestAnimationFrame(() => {
          this.controlFrame = null;
          this.updateControls();
        });
      }

      queueLayoutUpdate() {
        if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = null;
          this.rebuildPagination();
          this.updateControls();
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        if (this.controlFrame) cancelAnimationFrame(this.controlFrame);
        if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
        this.initialized = false;
      }
    }
  );
}

